import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { uploadRecording } from '../services/recordingsService';
import {
  ChevronRight,
  Volume2,
  Mic,
  Square,
  Sparkles,
  RotateCcw,
  Timer,
  Send,
  Headphones,
  Voicemail,
  BookOpen,
  HelpCircle,
  Star,
  CheckCircle2,
  Play,
  MessageCircle,
  Newspaper,
  Presentation,
} from 'lucide-react';

/* fr-FR speech */
const speak = (text, rate = 0.95) => {
  if (typeof window === 'undefined' || !window.speechSynthesis) return;
  try {
    window.speechSynthesis.cancel();
    const u = new window.SpeechSynthesisUtterance(text);
    u.lang = 'fr-FR';
    u.rate = rate;
    window.speechSynthesis.speak(u);
  } catch { /* silent */ }
};

/* -----------------------------------------------------------
   Free speaking — A1 & A2
   Structure per level: 5 activities × 5 prompts
----------------------------------------------------------- */
const ACTIVITIES_A1 = [
  {
    id: 'ecoute-reagis',
    emoji: '🎧',
    title: 'Écoute & Réagis',
    icon: Headphones,
    subtitle: 'Écoute un court message audio, puis réagis naturellement.',
    pace: '20–40 secondes',
    promptLabel: 'Situation',
    taskLabel: 'Réponds librement.',
    prompts: [
      { id: 'er-1', audio: 'Bonjour ! Aujourd\'hui, il fait très beau. Je pense aller au parc cet après-midi. Et toi, qu\'est-ce que tu vas faire aujourd\'hui ?' },
      { id: 'er-2', audio: 'Hier, j\'ai mangé une très bonne pizza avec mes amis. J\'ai mis trois garnitures sur ma pizza : oignons, maïs et champignons. Et toi, quelle est ta garniture préférée ? Décris comment tu souhaites ta pizza.' },
      { id: 'er-3', audio: 'Ce matin, je me suis réveillé très tôt. À quelle heure te lèves-tu normalement ?' },
      { id: 'er-4', audio: 'J\'aime beaucoup écouter de la musique quand je suis à la maison. Et toi ?' },
      { id: 'er-5', audio: 'Aujourd\'hui, je suis très content. Comment te sens-tu aujourd\'hui ?' },
    ],
  },
  {
    id: 'message-vocal',
    emoji: '🎙️',
    title: 'Message vocal reçu',
    icon: Voicemail,
    subtitle: 'Tu reçois un message vocal d\'un(e) ami(e). Réponds-lui.',
    pace: '20–45 secondes',
    promptLabel: 'Message',
    taskLabel: 'Réponds au message.',
    prompts: [
      { id: 'mv-1', audio: 'Salut ! Je ne sais pas quoi faire ce week-end. Tu as une idée ?' },
      { id: 'mv-2', audio: 'Bonjour ! Je cherche un bon film à regarder ce soir. Tu me conseilles quoi ?' },
      { id: 'mv-3', audio: 'Salut ! Quel est ton endroit préféré dans ta ville ?' },
      { id: 'mv-4', audio: 'Je voudrais commencer un nouveau loisir. Qu\'est-ce que tu me proposes ?' },
      { id: 'mv-5', audio: 'Quel est ton jour préféré de la semaine ? Pourquoi ?' },
    ],
  },
  {
    id: 'continue-histoire',
    emoji: '🎭',
    title: 'Continue l\'histoire',
    icon: BookOpen,
    subtitle: 'Tu entends le début d\'une histoire. Invente la suite.',
    pace: '30–45 secondes',
    promptLabel: 'Histoire',
    taskLabel: 'Continue l\'histoire.',
    prompts: [
      { id: 'ch-1', audio: 'Hier, en rentrant chez moi, j\'ai trouvé une petite boîte devant ma porte…' },
      { id: 'ch-2', audio: 'Un matin, je me suis réveillé et il n\'y avait plus d\'électricité dans toute la maison…' },
      { id: 'ch-3', audio: 'Au parc, j\'ai rencontré quelqu\'un de très sympathique…' },
      { id: 'ch-4', audio: 'Pendant une promenade, j\'ai trouvé quelque chose d\'étrange dans la rue…' },
      { id: 'ch-5', audio: 'Un jour, mon téléphone a commencé à sonner, mais je ne connaissais pas le numéro…' },
    ],
  },
  {
    id: 'question-surprise',
    emoji: '❓',
    title: 'Question surprise',
    icon: HelpCircle,
    subtitle: 'Une question au hasard. Pas de préparation — réagis immédiatement.',
    pace: '20–40 secondes',
    promptLabel: 'Question',
    taskLabel: 'Réponds spontanément.',
    prompts: [
      { id: 'qs-1', audio: 'Qu\'est-ce que tu as mangé aujourd\'hui ?' },
      { id: 'qs-2', audio: 'Quel est ton meilleur souvenir de cette semaine ?' },
      { id: 'qs-3', audio: 'Avec qui aimes-tu passer du temps ?' },
      { id: 'qs-4', audio: 'Qu\'est-ce que tu aimerais faire demain ?' },
      { id: 'qs-5', audio: 'Quel est ton endroit préféré pour te détendre ?' },
    ],
  },
  {
    id: 'opinion-flash',
    emoji: '🌟',
    title: 'Opinion flash',
    icon: Star,
    subtitle: 'Donne une opinion simple et personnelle.',
    pace: '20–40 secondes',
    promptLabel: 'Sujet',
    taskLabel: 'Donne ton opinion.',
    prompts: [
      { id: 'of-1', audio: 'Tu préfères le matin ou le soir ? Pourquoi ?' },
      { id: 'of-2', audio: 'Tu préfères voyager en train ou en voiture ?' },
      { id: 'of-3', audio: 'Tu préfères regarder un film ou lire un livre ?' },
      { id: 'of-4', audio: 'Tu préfères cuisiner ou commander un repas ?' },
      { id: 'of-5', audio: 'Tu préfères vivre près de la mer ou près de la montagne ?' },
    ],
  },
];

const ACTIVITIES_A2 = [
  {
    id: 'ecoute-reagis',
    emoji: '🎧',
    title: 'Écoute & Réagis',
    icon: Headphones,
    subtitle: 'Écoute un court message audio, puis raconte une expérience personnelle.',
    pace: '45–90 secondes',
    promptLabel: 'Situation',
    taskLabel: 'Réponds en développant une idée personnelle.',
    prompts: [
      { id: 'er-1', audio: 'Aujourd\'hui, j\'ai retrouvé une vieille photo de mon enfance. Cela m\'a rappelé beaucoup de souvenirs.', task: 'Parle d\'un souvenir que tu n\'oublieras jamais.' },
      { id: 'er-2', audio: 'Mon week-end a été complètement différent de ce que j\'avais prévu.', task: 'Raconte un week-end inattendu.' },
      { id: 'er-3', audio: 'J\'aimerais apprendre quelque chose de nouveau cette année.', task: 'Parle d\'une compétence que tu voudrais développer.' },
      { id: 'er-4', audio: 'Hier, j\'ai rencontré quelqu\'un après plusieurs années sans nouvelles.', task: 'Raconte une rencontre importante dans ta vie.' },
      { id: 'er-5', audio: 'Si je pouvais revivre une journée de ma vie, je choisirais sans hésiter une journée très spéciale.', task: 'Quelle journée aimerais-tu revivre ?' },
    ],
  },
  {
    id: 'message-vocal',
    emoji: '🎙️',
    title: 'Message vocal reçu',
    icon: Voicemail,
    subtitle: 'Tu reçois un message vocal. Réponds avec conseils, opinions et nuances.',
    pace: '45–90 secondes',
    promptLabel: 'Message',
    taskLabel: 'Réponds au message.',
    prompts: [
      { id: 'mv-1', audio: 'Salut ! Je voudrais changer quelque chose dans ma routine quotidienne. Tu me conseilles quoi ?', task: 'Réponds avec des idées et des conseils.' },
      { id: 'mv-2', audio: 'J\'hésite entre voyager seul ou partir avec des amis. Qu\'en penses-tu ?', task: 'Donne ton opinion.' },
      { id: 'mv-3', audio: 'J\'aimerais commencer un nouveau loisir mais je ne sais pas lequel choisir.', task: 'Fais des suggestions.' },
      { id: 'mv-4', audio: 'Je vais bientôt déménager dans une autre ville.', task: 'Donne des conseils et partage ton expérience.' },
      { id: 'mv-5', audio: 'Je voudrais améliorer mon français plus rapidement.', task: 'Donne plusieurs recommandations.' },
    ],
  },
  {
    id: 'continue-histoire',
    emoji: '🎭',
    title: 'Continue l\'histoire',
    icon: BookOpen,
    subtitle: 'Tu entends le début d\'une histoire plus complexe. Imagine et raconte la suite.',
    pace: '45–90 secondes',
    promptLabel: 'Histoire',
    taskLabel: 'Continue l\'histoire.',
    prompts: [
      { id: 'ch-1', audio: 'Pendant mes vacances, j\'ai reçu un message qui a complètement changé mes projets…' },
      { id: 'ch-2', audio: 'Lorsque je suis arrivé à la gare, j\'ai compris que quelque chose n\'allait pas se passer comme prévu…' },
      { id: 'ch-3', audio: 'Un soir, en rangeant ma chambre, j\'ai découvert un objet que j\'avais complètement oublié…' },
      { id: 'ch-4', audio: 'Je pensais passer une journée ordinaire, mais quelque chose d\'incroyable est arrivé…' },
      { id: 'ch-5', audio: 'En ouvrant ma boîte mail, j\'ai trouvé un message très surprenant…' },
    ],
  },
  {
    id: 'question-surprise',
    emoji: '❓',
    title: 'Question surprise',
    icon: HelpCircle,
    subtitle: 'Une question plus introspective. Réagis immédiatement et développe.',
    pace: '45–90 secondes',
    promptLabel: 'Question',
    taskLabel: 'Réponds spontanément et développe ta réponse.',
    prompts: [
      { id: 'qs-1', audio: 'Quelle est la meilleure décision que tu as prise récemment ?' },
      { id: 'qs-2', audio: 'Quel est le moment dont tu es le plus fier cette année ?' },
      { id: 'qs-3', audio: 'Si tu pouvais changer une habitude dans ta vie, laquelle choisirais-tu ?' },
      { id: 'qs-4', audio: 'Quelle activité te fait oublier le temps qui passe ?' },
      { id: 'qs-5', audio: 'Quel conseil donnerais-tu à ton toi d\'il y a cinq ans ?' },
    ],
  },
  {
    id: 'opinion-flash',
    emoji: '🌟',
    title: 'Opinion flash',
    icon: Star,
    subtitle: 'Donne une opinion personnelle avec des arguments simples.',
    pace: '45–90 secondes',
    promptLabel: 'Sujet',
    taskLabel: 'Donne ton opinion et justifie-la.',
    prompts: [
      { id: 'of-1', audio: 'Est-il préférable d\'économiser son argent ou de profiter de la vie immédiatement ?' },
      { id: 'of-2', audio: 'Les téléphones nous rapprochent-ils vraiment des autres ?' },
      { id: 'of-3', audio: 'Est-il important de sortir de sa zone de confort ?' },
      { id: 'of-4', audio: 'Peut-on apprendre autant en voyageant qu\'à l\'école ?' },
      { id: 'of-5', audio: 'Est-il préférable d\'avoir beaucoup d\'amis ou quelques vrais amis ?' },
    ],
  },
];

const ACTIVITIES_B1 = [
  {
    id: 'ecoute-reagis',
    emoji: '🎧',
    title: 'Écoute & Réagis',
    icon: Headphones,
    subtitle: 'Écoute une situation d\'actualité, puis réagis avec ton opinion argumentée.',
    pace: '60–120 secondes',
    promptLabel: 'Situation',
    taskLabel: 'Réagis avec une opinion argumentée.',
    prompts: [
      { id: 'er-1', audio: 'Les prix de nombreux produits alimentaires ont augmenté ces derniers mois. Beaucoup de familles cherchent à réduire leurs dépenses.', task: 'Comment gères-tu ton budget ? Quelles solutions peuvent aider les consommateurs ?' },
      { id: 'er-2', audio: 'Dans certaines villes, de plus en plus de commerces locaux ferment face à la concurrence des grandes plateformes en ligne.', task: 'Que penses-tu de cette situation ?' },
      { id: 'er-3', audio: 'De nombreux jeunes souhaitent aujourd\'hui travailler moins pour avoir davantage de temps libre.', task: 'Es-tu d\'accord avec cette tendance ?' },
      { id: 'er-4', audio: 'Plusieurs villes investissent dans davantage de pistes cyclables et de transports écologiques.', task: 'Est-ce une bonne priorité selon toi ?' },
      { id: 'er-5', audio: 'De plus en plus de consommateurs choisissent d\'acheter des produits d\'occasion plutôt que des produits neufs.', task: 'Pourquoi cette pratique devient-elle populaire ?' },
    ],
  },
  {
    id: 'message-vocal',
    emoji: '🎙️',
    title: 'Message vocal reçu',
    icon: Voicemail,
    subtitle: 'Tu reçois un message vocal. Réponds avec des conseils nuancés et argumentés.',
    pace: '60–120 secondes',
    promptLabel: 'Message',
    taskLabel: 'Réponds au message avec des conseils nuancés.',
    prompts: [
      { id: 'mv-1', audio: 'Salut ! Je dépense trop d\'argent chaque mois et j\'aimerais économiser davantage. Tu as des conseils ?' },
      { id: 'mv-2', audio: 'Je voudrais acheter local mais les produits sont souvent plus chers. Qu\'en penses-tu ?' },
      { id: 'mv-3', audio: 'J\'hésite entre travailler dans une grande entreprise ou dans une petite structure.' },
      { id: 'mv-4', audio: 'Je trouve que ma ville devient de plus en plus bruyante. Que pourrait-on faire ?' },
      { id: 'mv-5', audio: 'Beaucoup de gens autour de moi changent souvent de téléphone ou d\'ordinateur. Est-ce vraiment nécessaire ?' },
    ],
  },
  {
    id: 'continue-histoire',
    emoji: '🎭',
    title: 'Continue l\'histoire',
    icon: BookOpen,
    subtitle: 'Tu entends le début d\'une situation. Imagine la suite, les enjeux et tes réactions.',
    pace: '60–120 secondes',
    promptLabel: 'Histoire',
    taskLabel: 'Continue l\'histoire et développe tes réactions.',
    prompts: [
      { id: 'ch-1', audio: 'Un matin, en consultant les informations locales, j\'ai appris qu\'un grand projet allait transformer complètement mon quartier…' },
      { id: 'ch-2', audio: 'Lors d\'une réunion de citoyens, plusieurs habitants ont proposé des idées pour améliorer la ville…' },
      { id: 'ch-3', audio: 'Après plusieurs mois d\'économies, j\'ai finalement dû prendre une décision importante concernant mes dépenses…' },
      { id: 'ch-4', audio: 'Une entreprise de ma région a annoncé un projet innovant qui a suscité beaucoup de réactions…' },
      { id: 'ch-5', audio: 'En discutant avec des amis, nous avons réalisé que nos habitudes de consommation avaient beaucoup changé ces dernières années…' },
    ],
  },
  {
    id: 'question-surprise',
    emoji: '❓',
    title: 'Question surprise',
    icon: HelpCircle,
    subtitle: 'Une question sur la société, la ville ou la consommation. Développe une réponse argumentée.',
    pace: '60–120 secondes',
    promptLabel: 'Question',
    taskLabel: 'Réponds spontanément avec une argumentation claire.',
    prompts: [
      { id: 'qs-1', audio: 'Quel problème devrait être résolu en priorité dans ta ville ?' },
      { id: 'qs-2', audio: 'Quelles dépenses sont vraiment indispensables aujourd\'hui ?' },
      { id: 'qs-3', audio: 'Comment imagines-tu le commerce dans dix ans ?' },
      { id: 'qs-4', audio: 'Quel changement améliorerait le plus la qualité de vie des habitants ?' },
      { id: 'qs-5', audio: 'Quels sont les avantages et les inconvénients de vivre dans une grande ville ?' },
    ],
  },
  {
    id: 'opinion-flash',
    emoji: '🌟',
    title: 'Opinion flash',
    icon: Star,
    subtitle: 'Donne une opinion argumentée sur un sujet de société.',
    pace: '60–120 secondes',
    promptLabel: 'Sujet',
    taskLabel: 'Donne ton opinion avec des arguments et un exemple.',
    prompts: [
      { id: 'of-1', audio: 'Consommer moins mais mieux est-il devenu une nécessité ?' },
      { id: 'of-2', audio: 'Les produits locaux méritent-ils d\'être privilégiés même lorsqu\'ils coûtent plus cher ?' },
      { id: 'of-3', audio: 'Les villes devraient-elles limiter davantage la circulation automobile ?' },
      { id: 'of-4', audio: 'Faut-il encourager davantage la réparation plutôt que le remplacement des objets ?' },
      { id: 'of-5', audio: 'Le bien-être personnel est-il devenu plus important que la réussite professionnelle ?' },
    ],
  },
];

const ACTIVITIES_B2 = [
  {
    id: 'audio-article',
    emoji: '🎧',
    title: 'Audio Article & Mini Exposé',
    icon: Headphones,
    subtitle: 'Écoute un article de 45–60 secondes. Résume, présente ton opinion, donne un exemple.',
    pace: '2–4 minutes',
    promptLabel: 'Article',
    taskLabel: 'Résume, présente ton opinion, donne un exemple.',
    sharedTasks: ['Résumez l\'article.', 'Présentez votre opinion.', 'Donnez un exemple.'],
    prompts: [
      { id: 'aa-1', title: 'Inflation', audio: 'L\'inflation a fortement marqué ces deux dernières années. Les prix de l\'alimentation, du logement et de l\'énergie ont augmenté plus vite que les salaires. Les ménages les plus modestes ressentent davantage cette pression et réduisent leurs dépenses essentielles. Les économistes débattent : faut-il privilégier la maîtrise des prix ou la relance du pouvoir d\'achat ? Les banques centrales hésitent entre durcir les taux et préserver la croissance. Pour beaucoup, l\'inflation révèle surtout une fragilité plus profonde du modèle économique actuel.' },
      { id: 'aa-2', title: 'Logement', audio: 'Dans de nombreuses villes européennes, se loger devient un parcours du combattant. Les loyers atteignent des sommets, les prix d\'achat dépassent les capacités de la classe moyenne, et les jeunes actifs renoncent à s\'installer là où ils travaillent. Les pouvoirs publics multiplient les annonces : encadrement des loyers, construction de logements sociaux, taxation des résidences secondaires. Mais les résultats tardent. La question du logement devient un enjeu démocratique, car elle façonne la mobilité, le travail et la cohésion sociale.' },
      { id: 'aa-3', title: 'Transition énergétique', audio: 'La transition énergétique s\'accélère partout en Europe. Les pays investissent massivement dans le solaire, l\'éolien et la rénovation des bâtiments. Pourtant, les défis sont immenses : sécuriser l\'approvisionnement, financer la transition sans pénaliser les ménages, accompagner les secteurs industriels les plus polluants. Certains plaident pour le retour au nucléaire, d\'autres pour la sobriété énergétique. La question n\'est plus de savoir si la transition aura lieu, mais à quel rythme et au prix de quels efforts collectifs.' },
      { id: 'aa-4', title: 'Consommation responsable', audio: 'La consommation responsable n\'est plus une niche. De plus en plus de consommateurs vérifient l\'origine des produits, comparent les empreintes carbone et privilégient les marques engagées. Mais ce mouvement reste limité par le pouvoir d\'achat : les produits durables coûtent souvent plus cher. Les grandes enseignes y voient une opportunité, parfois au risque du greenwashing. Les ONG demandent des règles plus strictes pour distinguer les véritables engagements des stratégies marketing. La consommation devient un acte politique du quotidien.' },
      { id: 'aa-5', title: 'Mobilité urbaine', audio: 'La mobilité urbaine se réinvente. Les centres-villes ferment progressivement aux voitures, les pistes cyclables se multiplient, les transports en commun gagnent en fréquence. Certaines villes vont plus loin : zones piétonnes étendues, vélos en libre-service, navettes autonomes. Les habitants apprécient le calme retrouvé, mais les commerçants s\'inquiètent et les périphéries se sentent oubliées. Penser la ville de demain, c\'est repenser non seulement les déplacements, mais aussi l\'équité territoriale et la qualité de vie.' },
      { id: 'aa-6', title: 'Pénurie de main-d\'œuvre', audio: 'De nombreux secteurs peinent à recruter : restauration, santé, bâtiment, industrie. La pénurie de main-d\'œuvre n\'est plus conjoncturelle, elle devient structurelle. Les entreprises augmentent les salaires, assouplissent les conditions de travail et investissent dans la formation. Pourtant, les candidats manquent à l\'appel. Les sociologues parlent d\'un changement profond du rapport au travail : les jeunes cherchent du sens, de l\'autonomie et un meilleur équilibre. Les employeurs doivent désormais séduire autant qu\'embaucher.' },
      { id: 'aa-7', title: 'Vieillissement de la population', audio: 'L\'Europe vieillit rapidement. Le nombre de retraités augmente, celui des actifs diminue, et les systèmes de protection sociale se trouvent fragilisés. Le débat se cristallise autour de l\'âge de départ à la retraite, du financement de la dépendance et des inégalités intergénérationnelles. Au-delà des chiffres, c\'est tout un modèle de société qui doit être repensé : comment intégrer les seniors dans la vie active, soutenir les aidants, et préserver la solidarité entre les générations dans une économie en mutation ?' },
      { id: 'aa-8', title: 'Commerce international', audio: 'Le commerce international traverse une période de turbulences. Les tensions géopolitiques, les sanctions économiques et la fragmentation des chaînes d\'approvisionnement remettent en cause des décennies de mondialisation. De nombreuses entreprises relocalisent une partie de leur production, par souci de sécurité plus que de coût. Les États soutiennent leurs industries stratégiques, parfois au prix d\'un protectionnisme assumé. Le commerce mondial ne disparaît pas, mais il se réorganise autour de nouveaux blocs et de nouvelles priorités.' },
      { id: 'aa-9', title: 'Tourisme durable', audio: 'Plusieurs destinations européennes croulent sous l\'afflux des touristes. Venise, Barcelone, Amsterdam ou Santorin doivent gérer une fréquentation massive qui dégrade les centres historiques et exaspère les habitants. Le tourisme durable cherche un nouvel équilibre : taxes de séjour, quotas de visiteurs, valorisation des arrière-pays. Le défi est immense, car le tourisme représente une part importante de l\'économie locale. Concilier accueil, préservation et qualité de vie devient l\'un des enjeux majeurs des prochaines décennies.' },
      { id: 'aa-10', title: 'Travail hybride', audio: 'Le travail hybride s\'est imposé dans de nombreuses entreprises. Quelques jours au bureau, le reste à distance : ce modèle permet plus de flexibilité, réduit les déplacements et améliore l\'équilibre entre vie professionnelle et vie personnelle. Mais il pose aussi des questions de cohésion d\'équipe, de management et d\'isolement. Certaines entreprises souhaitent un retour partiel au bureau, d\'autres généralisent le télétravail. La frontière entre vie professionnelle et privée devient plus floue, et la confiance s\'impose comme le nouveau pilier du management.' },
    ],
  },
  {
    id: 'ai-debate',
    emoji: '🎙️',
    title: 'AI Debate',
    icon: MessageCircle,
    subtitle: 'Engage un débat structuré : réponds à l\'AI, puis défends ta position face à ses contre-arguments.',
    pace: '2–3 minutes',
    promptLabel: 'Débat',
    taskLabel: 'Réponds aux trois arguments de l\'AI et défends ta position.',
    sharedTasks: [
      'Réponds au premier argument.',
      'Réponds au contre-argument.',
      'Propose une alternative ou nuance ta position.',
    ],
    prompts: [
      { id: 'ad-1', title: 'Semaine de quatre jours', audio: 'L\'AI dit : « Je pense que la semaine de quatre jours devrait devenir la norme dans toutes les entreprises. » — Puis : « Beaucoup d\'employeurs craignent une baisse de productivité et des coûts plus élevés. Que répondez-vous ? » — Enfin : « Cette mesure n\'est-elle pas réservée aux cadres et aux secteurs privilégiés ? »' },
      { id: 'ad-2', title: 'Limitation du tourisme', audio: 'L\'AI dit : « Je pense que les grandes villes devraient limiter fortement le tourisme. » — Puis : « Je comprends votre point de vue, mais le tourisme crée aussi des emplois. Que répondez-vous ? » — Enfin : « Certaines régions dépendent économiquement du tourisme. Existe-t-il une autre solution ? »' },
      { id: 'ad-3', title: 'Revenu universel', audio: 'L\'AI dit : « Le revenu universel est la seule réponse sérieuse à l\'automatisation. » — Puis : « Mais cela coûterait extrêmement cher et pourrait décourager le travail. Que répondez-vous ? » — Enfin : « Comment financer une telle mesure sans creuser les inégalités fiscales ? »' },
      { id: 'ad-4', title: 'Énergie nucléaire', audio: 'L\'AI dit : « L\'énergie nucléaire est indispensable pour réussir la transition climatique. » — Puis : « Les déchets radioactifs et les risques d\'accident restent pourtant immenses. Que répondez-vous ? » — Enfin : « Faut-il vraiment investir dans le nucléaire plutôt que dans les renouvelables ? »' },
      { id: 'ad-5', title: 'Voiture en centre-ville', audio: 'L\'AI dit : « Les voitures individuelles devraient être complètement interdites dans les centres-villes. » — Puis : « Mais beaucoup d\'habitants en dépendent pour travailler et se déplacer. Que répondez-vous ? » — Enfin : « Quelle alternative équitable proposez-vous pour les personnes âgées et les périphéries ? »' },
      { id: 'ad-6', title: 'Télétravail généralisé', audio: 'L\'AI dit : « Le télétravail devrait devenir la norme. » — Puis : « Le télétravail isole les salariés et affaiblit la culture d\'entreprise. Que répondez-vous ? » — Enfin : « Tous les métiers peuvent-ils vraiment être exercés à distance ? »' },
      { id: 'ad-7', title: 'Consommation locale', audio: 'L\'AI dit : « Privilégier la consommation locale devrait devenir un réflexe national. » — Puis : « Le local n\'est pas toujours plus écologique, et il coûte souvent plus cher. Que répondez-vous ? » — Enfin : « Comment rendre la consommation locale accessible à tous ?»' },
      { id: 'ad-8', title: 'IA au travail', audio: 'L\'AI dit : « L\'intelligence artificielle va remplacer la plupart des métiers intellectuels dans les vingt prochaines années. » — Puis : « Beaucoup pensent que l\'IA créera plus de métiers qu\'elle n\'en supprimera. Que répondez-vous ? » — Enfin : « Comment préparer la société à cette transformation sans creuser les inégalités ? »' },
      { id: 'ad-9', title: 'Enseignement à distance', audio: 'L\'AI dit : « L\'enseignement à distance est l\'avenir de l\'éducation. » — Puis : « L\'école joue aussi un rôle social et émotionnel difficile à remplacer en ligne. Que répondez-vous ? » — Enfin : « Comment éviter que la distance n\'aggrave les inégalités scolaires ? »' },
      { id: 'ad-10', title: 'Protectionnisme économique', audio: 'L\'AI dit : « Les pays européens devraient adopter un protectionnisme plus assumé. » — Puis : « Le protectionnisme augmente souvent les prix et freine l\'innovation. Que répondez-vous ? » — Enfin : « Comment protéger l\'économie sans renoncer à la coopération internationale ? »' },
    ],
  },
  {
    id: 'reaction-actualite',
    emoji: '📰',
    title: 'Réaction à l\'actualité',
    icon: Newspaper,
    subtitle: 'Écoute un court reportage. Analyse les enjeux, donne ton avis, propose des solutions.',
    pace: '2–3 minutes',
    promptLabel: 'Reportage',
    taskLabel: 'Analyse, prends position, propose des solutions.',
    sharedTasks: [
      'Quels sont les enjeux ?',
      'Êtes-vous d\'accord ?',
      'Quelles solutions proposeriez-vous ?',
    ],
    prompts: [
      { id: 'ra-1', title: 'Économie mondiale', audio: 'Selon les dernières prévisions, la croissance mondiale ralentit. Les économies développées ralentissent, certaines grandes puissances entrent en stagnation, tandis que plusieurs pays émergents poursuivent leur dynamique. Les économistes parlent d\'un monde de plus en plus fragmenté, où les grandes décisions économiques deviennent aussi politiques.' },
      { id: 'ra-2', title: 'Logement', audio: 'Dans plusieurs grandes villes, le nombre de logements vacants augmente alors que la demande explose. Les locations courtes durées, les résidences secondaires et la spéculation immobilière alimentent une crise structurelle. Les jeunes actifs renoncent à vivre près de leur travail, et la mobilité résidentielle ralentit.' },
      { id: 'ra-3', title: 'Énergie', audio: 'Plusieurs pays revoient leur stratégie énergétique. L\'investissement dans le solaire, l\'éolien et le stockage atteint des niveaux records. Pourtant, la dépendance aux énergies fossiles reste forte, et la facture énergétique pèse encore lourdement sur les ménages les plus modestes.' },
      { id: 'ra-4', title: 'Consommation', audio: 'Les habitudes de consommation changent rapidement. Le marché de l\'occasion explose, la réparation des objets gagne du terrain, et de nombreux consommateurs privilégient les marques locales. Les enseignes traditionnelles s\'adaptent, parfois en douleur, à ces nouvelles attentes.' },
      { id: 'ra-5', title: 'Travail', audio: 'Les démissions atteignent des niveaux historiques dans plusieurs secteurs. Les salariés cherchent davantage de sens, de flexibilité et de reconnaissance. Les entreprises multiplient les avantages, repensent l\'organisation et tentent de retenir les talents dans un marché du travail tendu.' },
      { id: 'ra-6', title: 'Innovation', audio: 'L\'intelligence artificielle se diffuse dans tous les secteurs : santé, éducation, finance, industrie. Les promesses sont immenses, mais les inquiétudes aussi : éthique, emplois, vie privée, désinformation. Les régulateurs cherchent à encadrer ces technologies sans freiner l\'innovation.' },
      { id: 'ra-7', title: 'Démographie', audio: 'La natalité baisse dans de nombreux pays européens, tandis que l\'espérance de vie continue d\'augmenter. Les systèmes de retraite, de santé et d\'éducation se réorganisent. Les politiques familiales et migratoires reviennent au cœur du débat public.' },
      { id: 'ra-8', title: 'Transition climatique', audio: 'Les épisodes climatiques extrêmes se multiplient : canicules, inondations, sécheresses. Les coûts pour les collectivités et les assurances explosent. La transition climatique n\'est plus un projet lointain, c\'est une urgence économique et sociale qui transforme les territoires.' },
    ],
  },
  {
    id: 'mini-conference',
    emoji: '🎤',
    title: 'Mini Conférence',
    icon: Presentation,
    subtitle: 'Vous disposez de trois minutes pour présenter votre réflexion structurée sur le sujet.',
    pace: '3 minutes',
    promptLabel: 'Conférence',
    taskLabel: 'Présentez une réflexion structurée et nuancée pendant trois minutes.',
    sharedTasks: ['Vous disposez de trois minutes pour présenter votre réflexion sur ce sujet.'],
    prompts: [
      { id: 'mc-1', title: 'L\'avenir du travail', audio: 'Vous disposez de trois minutes pour présenter votre réflexion sur le sujet suivant : l\'avenir du travail.' },
      { id: 'mc-2', title: 'Les villes de demain', audio: 'Vous disposez de trois minutes pour présenter votre réflexion sur le sujet suivant : les villes de demain.' },
      { id: 'mc-3', title: 'Les défis économiques', audio: 'Vous disposez de trois minutes pour présenter votre réflexion sur le sujet suivant : les défis économiques actuels et à venir.' },
      { id: 'mc-4', title: 'L\'entrepreneuriat', audio: 'Vous disposez de trois minutes pour présenter votre réflexion sur le sujet suivant : l\'entrepreneuriat aujourd\'hui.' },
      { id: 'mc-5', title: 'Les nouvelles formes de consommation', audio: 'Vous disposez de trois minutes pour présenter votre réflexion sur le sujet suivant : les nouvelles formes de consommation.' },
      { id: 'mc-6', title: 'Les politiques environnementales', audio: 'Vous disposez de trois minutes pour présenter votre réflexion sur le sujet suivant : les politiques environnementales contemporaines.' },
    ],
  },
];

const ACTIVITIES_BY_LEVEL = { A1: ACTIVITIES_A1, A2: ACTIVITIES_A2, B1: ACTIVITIES_B1, B2: ACTIVITIES_B2 };
const ENABLED_LEVELS = ['A1', 'A2', 'B1', 'B2'];
const ALL_LEVELS = ['A1', 'A2', 'B1', 'B2'];

const TIMER_MODES_BY_LEVEL = {
  A1: [
    { id: 'easy', name: 'Découverte', seconds: 20, sub: '20 sec' },
    { id: 'standard', name: 'Standard', seconds: 30, sub: '30 sec' },
    { id: 'challenge', name: 'Défi', seconds: 45, sub: '45 sec' },
  ],
  A2: [
    { id: 'standard', name: 'Standard', seconds: 45, sub: '45 sec' },
    { id: 'challenge', name: 'Challenge', seconds: 60, sub: '60 sec' },
    { id: 'expert', name: 'Expert', seconds: 90, sub: '90 sec' },
  ],
  B1: [
    { id: 'standard', name: 'Standard', seconds: 60, sub: '1 min' },
    { id: 'challenge', name: 'Challenge', seconds: 90, sub: '1 min 30' },
    { id: 'expert', name: 'Expert', seconds: 120, sub: '2 min' },
  ],
  B2: [
    { id: 'standard', name: 'Standard', seconds: 120, sub: '2 min' },
    { id: 'challenge', name: 'Challenge', seconds: 180, sub: '3 min' },
    { id: 'expert', name: 'Expert', seconds: 240, sub: '4 min' },
  ],
};

const DEFAULT_TIMER_ID_BY_LEVEL = { A1: 'standard', A2: 'challenge', B1: 'challenge', B2: 'challenge' };

const FEEDBACK_METRICS_BY_LEVEL = {
  A1: ['Prononciation', 'Fluidité', 'Clarté'],
  A2: ['Prononciation', 'Fluidité', 'Utilisation du passé', 'Utilisation du futur', 'Richesse du vocabulaire', 'Organisation des idées'],
  B1: ['Prononciation', 'Fluidité', 'Argumentation', 'Cohérence', 'Richesse du vocabulaire', 'Précision grammaticale'],
  B2: ['Prononciation', 'Fluidité', 'Argumentation', 'Nuance', 'Richesse lexicale', 'Registre formel'],
};

const TEACHER_MESSAGE_BY_LEVEL = {
  A1: {
    intro: 'Votre réponse a été enregistrée. Votre professeur analysera votre production orale.',
    bulletsLabel: 'Vous retrouverez dans votre tableau de bord :',
    bullets: [
      'commentaires personnalisés',
      'corrections éventuelles',
      'conseils de prononciation',
      'suggestions de vocabulaire',
    ],
    closing: 'Continuez à parler régulièrement pour gagner en confiance et en fluidité.',
  },
  A2: {
    intro: 'Votre production orale a été enregistrée. Votre professeur analysera votre réponse.',
    bulletsLabel: null,
    bullets: null,
    closing: 'Les commentaires détaillés, les corrections et les recommandations seront disponibles dans votre tableau de bord.',
  },
  B1: {
    intro: 'Votre prise de parole a été transmise à votre professeur. Une analyse approfondie de votre argumentation sera ajoutée à votre tableau de bord.',
    bulletsLabel: 'Vous y retrouverez :',
    bullets: [
      'qualité de l\'argumentation et des exemples',
      'précision grammaticale (temps, accords, connecteurs)',
      'richesse et justesse du vocabulaire',
      'cohérence et structure du discours',
      'pistes d\'amélioration pour gagner en nuance',
    ],
    closing: 'Continuez à argumenter, comparer et illustrer : c\'est ainsi que se construit une parole convaincante.',
  },
  B2: {
    intro: 'Votre production orale a été enregistrée. Votre professeur réalisera une analyse avancée de votre prise de parole.',
    bulletsLabel: 'Votre tableau de bord inclura :',
    bullets: [
      'qualité de l\'argumentation et de la structure du discours',
      'maîtrise du registre formel et académique',
      'nuance, contre-arguments et capacité à hiérarchiser',
      'richesse lexicale et précision idiomatique',
      'fluidité, prononciation et intonation',
      'pistes d\'amélioration ciblées pour un niveau DELF B2',
    ],
    closing: 'Continuez à débattre, exposer et nuancer : c\'est l\'esprit DELF B2 qui se construit, prise de parole après prise de parole.',
  },
};

const OBJECTIVE_BY_LEVEL = {
  A1: {
    title: 'Objectif A1',
    intro: 'L\'apprenant doit apprendre à :',
    items: [
      'répondre spontanément',
      'comprendre une question simple',
      'réagir à un message oral',
      'raconter une idée simple',
      'maintenir une conversation courte et naturelle en français',
    ],
    closing: 'Pas de plan, pas de structure : c\'est la spontanéité qui construit la confiance.',
  },
  A2: {
    title: 'Objectif A2',
    intro: 'À la fin du niveau A2, l\'apprenant doit être capable de :',
    items: [
      'raconter une expérience',
      'parler d\'un souvenir',
      'exprimer une émotion',
      'expliquer un choix',
      'parler de projets futurs',
      'développer une réponse pendant 1 à 2 minutes',
    ],
    closing: 'Avec plus d\'aisance et de spontanéité — sans plan, sans structure imposée.',
  },
  B1: {
    title: 'Objectif B1',
    intro: 'À la fin du niveau B1, l\'apprenant doit être capable de :',
    items: [
      'réagir spontanément à un sujet d\'actualité',
      'donner une opinion argumentée sur la société, la ville, la consommation',
      'comparer plusieurs points de vue',
      'illustrer ses idées avec des exemples concrets',
      'proposer des solutions et nuancer ses propos',
      'développer une réponse pendant 1 à 2 minutes en discours fluide',
    ],
    closing: 'Pas de plan, pas de structure imposée — c\'est l\'argumentation spontanée qui construit la maîtrise.',
  },
  B2: {
    title: 'Objectif B2',
    intro: 'À la fin du niveau B2, l\'apprenant doit être capable de :',
    items: [
      'résumer et exposer un article complexe',
      'défendre une position face à des contre-arguments (AI Debate)',
      'analyser une actualité et proposer des solutions structurées',
      'présenter une mini-conférence de 3 minutes sur un sujet sociétal',
      'maîtriser le registre formel et la nuance argumentative',
      'parler 2 à 4 minutes avec aisance, fluidité et précision lexicale',
    ],
    closing: 'C\'est le niveau DELF B2 oral : argumenter, nuancer, exposer et débattre comme un locuteur autonome.',
  },
};

const SUBTITLE_BY_LEVEL = {
  A1: '25 prompts A1 — pas de plan, pas de structure. Écoute, réagis, parle. Apprends à penser et à répondre directement en français.',
  A2: '25 prompts A2 — raconter une expérience, exprimer une émotion, parler du passé, parler du futur, expliquer ses choix. Communication spontanée et développée.',
  B1: '25 prompts B1 — actualité, société, ville, consommation. Réagis, argumente, compare, illustre — 1 à 2 minutes de parole spontanée et structurée.',
  B2: '34 prompts B2 — Audio Article, AI Debate, Réaction à l\'actualité, Mini Conférence. 2 à 4 minutes de prise de parole DELF B2 — argumenter, nuancer, exposer.',
};

const TRICKY_WORDS = ['aujourd\'hui', 'préféré', 'téléphone', 'parc', 'musique', 'samedi', 'cuisiner', 'commander', 'montagne', 'restaurant'];

/* Simulated AI feedback (Coqui-style) — produces a score per metric for the active level */
function makeFeedback(level) {
  const labels = FEEDBACK_METRICS_BY_LEVEL[level] || FEEDBACK_METRICS_BY_LEVEL.A1;
  const base = 70 + Math.floor(Math.random() * 22);
  const scores = labels.map((label) => ({
    label,
    value: Math.min(98, Math.max(60, base + (Math.floor(Math.random() * 12) - 6))),
  }));
  const tricky = [];
  while (tricky.length < 2) {
    const w = TRICKY_WORDS[Math.floor(Math.random() * TRICKY_WORDS.length)];
    if (!tricky.includes(w)) tricky.push(w);
  }
  return { scores, tricky };
}

/* Recorder hook */
function useRecorder() {
  const [recording, setRecording] = useState(false);
  const [audioUrl, setAudioUrl] = useState(null);
  const [audioBlob, setAudioBlob] = useState(null);
  const mrRef = useRef(null);
  const chunksRef = useRef([]);

  const start = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream);
      chunksRef.current = [];
      mr.ondataavailable = (e) => chunksRef.current.push(e.data);
      mr.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: 'audio/webm' });
        setAudioBlob(blob);
        setAudioUrl(URL.createObjectURL(blob));
        stream.getTracks().forEach((t) => t.stop());
      };
      mrRef.current = mr;
      mr.start();
      setRecording(true);
      return true;
    } catch {
      alert('Microphone non disponible — vérifie les permissions de ton navigateur.');
      return false;
    }
  }, []);

  const stop = useCallback(() => {
    if (mrRef.current && recording) {
      mrRef.current.stop();
      setRecording(false);
    }
  }, [recording]);

  const reset = useCallback(() => {
    setAudioUrl(null);
    setAudioBlob(null);
    chunksRef.current = [];
  }, []);

  return { recording, audioUrl, audioBlob, start, stop, reset };
}

/* -----------------------------------------------------------
   Prompt detail — listen / record / feedback / submit
----------------------------------------------------------- */
function PromptDetail({ activity, prompt, timerSeconds, level = 'A1' }) {
  const { recording, audioUrl, audioBlob, start, stop, reset } = useRecorder();
  const [remaining, setRemaining] = useState(timerSeconds);
  const [phase, setPhase] = useState('idle');
  const [feedback, setFeedback] = useState(null);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const tickRef = useRef(null);

  // Reset when prompt or timer changes
  useEffect(() => {
    setRemaining(timerSeconds);
    setPhase('idle');
    setFeedback(null);
    setSubmitted(false);
    setSubmitError('');
    reset();
    if (tickRef.current) clearInterval(tickRef.current);
    if (recording) stop();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prompt.id, timerSeconds]);

  useEffect(() => () => { if (tickRef.current) clearInterval(tickRef.current); }, []);

  const handleStart = async () => {
    setFeedback(null);
    reset();
    setRemaining(timerSeconds);
    const ok = await start();
    if (!ok) return;
    setPhase('recording');
    tickRef.current = setInterval(() => {
      setRemaining((r) => {
        if (r <= 1) {
          clearInterval(tickRef.current);
          stop();
          setPhase('done');
          setFeedback(makeFeedback(level));
          return 0;
        }
        return r - 1;
      });
    }, 1000);
  };

  const handleStopEarly = () => {
    if (tickRef.current) clearInterval(tickRef.current);
    stop();
    setPhase('done');
    setFeedback(makeFeedback(level));
  };

  const handleReset = () => {
    if (tickRef.current) clearInterval(tickRef.current);
    setRemaining(timerSeconds);
    setPhase('idle');
    setFeedback(null);
    setSubmitted(false);
    setSubmitError('');
    reset();
  };

  const handleSubmit = async () => {
    if (!audioBlob) {
      setSubmitError("Aucun enregistrement à envoyer. Enregistre-toi d'abord.");
      return;
    }
    setSubmitting(true);
    setSubmitError('');
    try {
      await uploadRecording(audioBlob, {
        level,
        exerciseId: activity.id || `${level}-${activity.title}`.toLowerCase(),
        exerciseTitle: `${activity.title} — Expression orale libre`,
        promptId: String(prompt.id || ''),
        promptText: prompt.text || prompt.title || '',
        durationSeconds: timerSeconds - remaining,
      });
      setSubmitted(true);
    } catch (err) {
      setSubmitError(err.message || 'Envoi impossible. Réessaie dans un instant.');
    } finally {
      setSubmitting(false);
    }
  };

  const progress = ((timerSeconds - remaining) / timerSeconds) * 100;
  const Icon = activity.icon;

  return (
    <div className="space-y-3" data-testid={`prompt-detail-${prompt.id}`}>
      {/* Header */}
      <div className="bg-gradient-to-br from-[#1A2F4A] to-[#243a58] rounded-2xl px-6 py-5 text-white">
        <p className="text-[10px] text-[#D4AF37] uppercase tracking-[0.25em] font-bold mb-1.5 flex items-center gap-1.5">
          <Icon className="w-3.5 h-3.5" /> {activity.title} · {level}
        </p>
        {prompt.title && (
          <p className="text-[11px] text-white/80 uppercase tracking-[0.2em] font-bold mb-1" data-testid="prompt-topic">{prompt.title}</p>
        )}
        <h2
          className="text-xl sm:text-2xl font-bold leading-tight"
          style={{ fontFamily: "'Playfair Display', serif" }}
          data-testid="prompt-title"
        >
          <span className="text-[#D4AF37] mr-2">«</span>
          {prompt.audio}
          <span className="text-[#D4AF37] ml-2">»</span>
        </h2>
        <div className="mt-3 flex items-center gap-2">
          <button
            onClick={() => speak(prompt.audio)}
            className="inline-flex items-center gap-2 bg-[#D4AF37] hover:bg-white text-[#1A2F4A] rounded-full px-4 py-2 text-xs font-bold transition-colors"
            data-testid="prompt-listen"
          >
            <Volume2 className="w-3.5 h-3.5" /> Écouter
          </button>
          <span className="text-[11px] text-white/70 italic">Voix Coqui · fr-FR</span>
        </div>
      </div>

      {/* Task card */}
      <div className="bg-white border border-[#E8E4DC] rounded-2xl px-5 py-4" data-testid="prompt-task">
        <p className="text-[10px] text-[#D4AF37] uppercase tracking-[0.25em] font-bold mb-1">Ta tâche</p>
        {activity.sharedTasks && activity.sharedTasks.length > 1 ? (
          <ol className="space-y-1.5 list-none mt-1" data-testid="task-list">
            {activity.sharedTasks.map((t, i) => (
              <li key={i} className="flex items-start gap-2.5 text-[13px] text-[#1A2F4A] font-medium" data-testid={`task-item-${i}`}>
                <span className="w-5 h-5 rounded-full bg-[#FAF7F0] border border-[#D4AF37]/30 text-[10px] font-bold text-[#1A2F4A] flex items-center justify-center flex-shrink-0 mt-0.5">
                  {i + 1}
                </span>
                <span>{t}</span>
              </li>
            ))}
          </ol>
        ) : (
          <p className="text-[14px] text-[#1A2F4A] font-medium">{prompt.task || (activity.sharedTasks && activity.sharedTasks[0]) || activity.taskLabel}</p>
        )}
        <p className="text-[11px] text-[#6B4E37] italic mt-2">Pas de plan, pas de structure — réagis naturellement.</p>
      </div>

      {/* Record card */}
      <div className="bg-white border border-[#E8E4DC] rounded-2xl px-5 py-4 space-y-3" data-testid="record-card">
        <div className="flex items-center justify-between text-xs">
          <div className="inline-flex items-center gap-1.5 text-[#6B4E37]">
            <Timer className="w-3.5 h-3.5 text-[#D4AF37]" />
            <span>{phase === 'recording' ? `${remaining}s restantes` : `${timerSeconds}s pour parler`}</span>
          </div>
          {phase === 'recording' && <span className="text-[#D4AF37] font-bold animate-pulse">● REC</span>}
        </div>
        <div className="w-full h-1.5 bg-[#F3F4F6] rounded-full overflow-hidden" data-testid="timer-bar">
          <div className="h-full rounded-full bg-[#D4AF37] transition-all duration-700" style={{ width: `${progress}%` }} />
        </div>

        {phase === 'idle' && (
          <button
            onClick={handleStart}
            className="w-full inline-flex items-center justify-center gap-2 bg-[#1A2F4A] hover:bg-[#243a58] text-white rounded-full py-3 text-sm font-bold transition-colors"
            data-testid="record-start"
          >
            <Mic className="w-4 h-4" /> Démarrer & m'enregistrer
          </button>
        )}
        {phase === 'recording' && (
          <button
            onClick={handleStopEarly}
            className="w-full inline-flex items-center justify-center gap-2 bg-red-500 hover:bg-red-600 text-white rounded-full py-3 text-sm font-bold animate-pulse"
            data-testid="record-stop"
          >
            <Square className="w-4 h-4" /> Arrêter
          </button>
        )}
        {phase === 'done' && (
          <button
            onClick={handleReset}
            className="w-full inline-flex items-center justify-center gap-2 bg-white border-2 border-[#1A2F4A] text-[#1A2F4A] hover:bg-[#FAF7F0] rounded-full py-3 text-sm font-bold transition-colors"
            data-testid="record-retry"
          >
            <RotateCcw className="w-4 h-4" /> Réessayer
          </button>
        )}

        {audioUrl && <audio controls src={audioUrl} className="w-full h-9" data-testid="playback" />}
      </div>

      {/* Feedback */}
      {feedback && (
        <div className="space-y-3" data-testid="feedback-panel">
          <div className="bg-[#1A2F4A] rounded-2xl p-4 text-white">
            <p className="text-[10px] text-[#D4AF37] uppercase tracking-wider font-bold mb-3 flex items-center gap-1.5">
              <Sparkles className="w-3.5 h-3.5" /> Analyse automatique
            </p>
            <div className={`grid gap-3 ${feedback.scores.length > 3 ? 'grid-cols-2' : 'grid-cols-3'}`}>
              {feedback.scores.map((m) => (
                <div key={m.label} data-testid={`metric-${m.label}`}>
                  <div className="flex justify-between text-[10px] text-white/70 mb-1 font-medium">
                    <span>{m.label}</span>
                    <span className="text-[#D4AF37] font-bold">{m.value}%</span>
                  </div>
                  <div className="w-full h-1.5 bg-white/10 rounded-full overflow-hidden">
                    <div className="h-full rounded-full bg-[#D4AF37] transition-all duration-700" style={{ width: `${m.value}%` }} />
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-3 pt-3 border-t border-white/10">
              <p className="text-[10px] text-[#D4AF37] uppercase tracking-wider font-bold mb-1">Mots difficiles</p>
              <div className="flex flex-wrap gap-1.5" data-testid="tricky-words">
                {feedback.tricky.map((w) => (
                  <span key={w} className="text-[11px] bg-white/10 text-white rounded-full px-2.5 py-0.5">{w}</span>
                ))}
              </div>
            </div>
            <div className="mt-3 text-[11px] text-white/70 italic">
              Temps de parole : {timerSeconds - remaining}s / {timerSeconds}s · Suggestions d'amélioration disponibles ci-dessous.
            </div>
          </div>

          {!submitted ? (
            <>
              <button
                onClick={handleSubmit}
                disabled={submitting || !audioBlob}
                className="w-full inline-flex items-center justify-center gap-2 bg-[#D4AF37] hover:bg-[#1A2F4A] text-[#1A2F4A] hover:text-[#D4AF37] rounded-full py-2.5 text-xs font-bold transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="submit-to-teacher"
              >
                <Send className="w-3.5 h-3.5" />
                {submitting ? 'Envoi en cours…' : 'Envoyer à mon professeur'}
              </button>
              {submitError && (
                <p className="text-[11px] text-red-600 mt-1.5" data-testid="submit-error">{submitError}</p>
              )}
            </>
          ) : (
            <div className="bg-emerald-50 border border-emerald-200 rounded-xl px-4 py-3" data-testid="teacher-confirmation">
              <p className="text-[11px] text-emerald-700 uppercase tracking-wider font-bold mb-1.5">📋 Réponse enregistrée</p>
              <p className="text-[12px] text-[#1A2F4A] leading-relaxed" data-testid="teacher-intro">
                {TEACHER_MESSAGE_BY_LEVEL[level].intro}
              </p>
              {TEACHER_MESSAGE_BY_LEVEL[level].bullets && (
                <>
                  <p className="text-[11px] text-[#6B4E37] uppercase tracking-wider font-bold mt-2.5 mb-1">
                    {TEACHER_MESSAGE_BY_LEVEL[level].bulletsLabel}
                  </p>
                  <ul className="space-y-1 list-none">
                    {TEACHER_MESSAGE_BY_LEVEL[level].bullets.map((b, i) => (
                      <li key={i} className="flex items-start gap-2 text-[12px] text-[#1A2F4A]" data-testid={`teacher-bullet-${i}`}>
                        <span className="text-[#D4AF37] mt-0.5 flex-shrink-0">•</span>
                        {b}
                      </li>
                    ))}
                  </ul>
                </>
              )}
              <p className="text-[12px] text-[#6B4E37] italic mt-2" data-testid="teacher-closing">
                {TEACHER_MESSAGE_BY_LEVEL[level].closing}
              </p>
            </div>
          )}
        </div>
      )}

      {!feedback && (
        <div className="bg-[#FAF7F0]/60 border border-[#E8E4DC] rounded-2xl px-5 py-3" data-testid="feedback-placeholder">
          <p className="text-[11px] text-[#6B4E37] italic flex items-center gap-1.5">
            <Sparkles className="w-3.5 h-3.5 text-[#D4AF37] flex-shrink-0" />
            Termine ton enregistrement pour recevoir une analyse {FEEDBACK_METRICS_BY_LEVEL[level].map((m) => m.toLowerCase()).slice(0, 3).join(', ')}.
          </p>
        </div>
      )}
    </div>
  );
}

/* ===========================================================
   MAIN PAGE
=========================================================== */
export default function ExpressionOraleLibre() {
  const [level, setLevel] = useState('A1');
  const activities = ACTIVITIES_BY_LEVEL[level];
  const timerModes = TIMER_MODES_BY_LEVEL[level];

  const [activityId, setActivityId] = useState(activities[0].id);
  const activity = activities.find((a) => a.id === activityId) || activities[0];
  const [promptId, setPromptId] = useState(activity.prompts[0].id);
  const [mode, setMode] = useState(DEFAULT_TIMER_ID_BY_LEVEL[level]);

  // Reset activity + prompt + timer mode when level changes
  useEffect(() => {
    const nextActivities = ACTIVITIES_BY_LEVEL[level];
    setActivityId(nextActivities[0].id);
    setPromptId(nextActivities[0].prompts[0].id);
    setMode(DEFAULT_TIMER_ID_BY_LEVEL[level]);
  }, [level]);

  // Reset prompt when activity changes
  useEffect(() => {
    setPromptId(activity.prompts[0].id);
  }, [activity]);

  const prompt = activity.prompts.find((p) => p.id === promptId) || activity.prompts[0];
  const timerSeconds = (timerModes.find((t) => t.id === mode) || timerModes[0]).seconds;

  const goToPrompt = (id) => {
    setPromptId(id);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const objective = OBJECTIVE_BY_LEVEL[level];

  return (
    <div className="bg-[#FAF7F0] min-h-screen">
      {/* Header */}
      <section className="pt-12 pb-6">
        <div className="max-w-[760px] mx-auto px-6">
          <div className="flex items-center gap-1.5 text-[10px] text-[#999] mb-6 flex-wrap" data-testid="breadcrumb">
            <Link to="/speaking-lab" className="hover:text-[#D4AF37] transition-colors">Speaking Lab</Link>
            <ChevronRight className="w-3 h-3" />
            <span>S'exprimer</span>
            <ChevronRight className="w-3 h-3" />
            <span className="text-[#1A2F4A] font-bold">Expression orale libre sur un sujet</span>
          </div>

          <div className="text-center mb-6">
            <div className="flex items-center justify-center gap-3 mb-3">
              <div className="h-px w-10 bg-[#D4AF37]/50" />
              <Mic className="w-5 h-5 text-[#D4AF37]" />
              <div className="h-px w-10 bg-[#D4AF37]/50" />
            </div>
            <h1
              className="text-3xl sm:text-4xl font-bold text-[#1A2F4A] mb-3"
              style={{ fontFamily: "'Playfair Display', serif" }}
              data-testid="page-title"
            >
              Expression orale libre sur un sujet
            </h1>
            <p className="text-sm text-[#6B4E37] italic max-w-md mx-auto leading-relaxed" data-testid="page-subtitle">
              {SUBTITLE_BY_LEVEL[level]}
            </p>

            {/* Level tabs (A1 + A2 + B1 + B2 active) */}
            <div className="flex justify-center mt-4">
              <div className="flex" data-testid="level-tabs">
                {ALL_LEVELS.map((lv, idx) => {
                  const isEnabled = ENABLED_LEVELS.includes(lv);
                  const isActive = level === lv;
                  const isFirst = idx === 0;
                  const isLast = idx === ALL_LEVELS.length - 1;
                  return (
                    <button
                      key={lv}
                      disabled={!isEnabled}
                      onClick={() => { if (isEnabled) setLevel(lv); }}
                      title={isEnabled ? '' : 'À venir'}
                      className={`text-[10px] font-bold tracking-wider px-3 py-1 transition-colors border ${
                        isActive
                          ? 'bg-[#1A2F4A] text-white border-[#1A2F4A]'
                          : isEnabled
                          ? 'bg-white text-[#1A2F4A] border-[#E8E4DC] hover:border-[#D4AF37]'
                          : 'bg-white text-[#999] border-[#E8E4DC] cursor-not-allowed opacity-60'
                      } ${isFirst ? 'rounded-l-full' : ''} ${isLast ? 'rounded-r-full' : ''} ${!isFirst ? '-ml-px' : ''}`}
                      data-testid={`level-${lv}`}
                    >
                      {lv}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Activity tabs */}
      <section className="pb-2">
        <div className="max-w-[760px] mx-auto px-6">
          <div className="bg-white border border-[#E8E4DC] rounded-2xl px-3 py-3" data-testid="activity-tabs">
            <p className="text-[10px] text-[#6B4E37] uppercase tracking-[0.2em] font-bold mb-2 px-2">Choisis une activité</p>
            <div className={`grid grid-cols-2 ${activities.length === 4 ? 'sm:grid-cols-4' : 'sm:grid-cols-5'} gap-1.5`}>
              {activities.map((a) => {
                const isActive = activityId === a.id;
                const Icon = a.icon;
                return (
                  <button
                    key={a.id}
                    onClick={() => setActivityId(a.id)}
                    className={`rounded-xl text-[11px] font-bold py-2.5 px-2 border transition-colors flex flex-col items-center gap-1 ${
                      isActive
                        ? 'bg-[#1A2F4A] text-white border-[#1A2F4A]'
                        : 'bg-white text-[#1A2F4A] border-[#E8E4DC] hover:border-[#D4AF37]'
                    }`}
                    data-testid={`activity-${a.id}`}
                  >
                    <Icon className={`w-4 h-4 ${isActive ? 'text-[#D4AF37]' : 'text-[#D4AF37]'}`} />
                    <span className="leading-tight text-center">{a.title}</span>
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* Activity description + timer modes */}
      <section className="pb-2 pt-3">
        <div className="max-w-[760px] mx-auto px-6 space-y-2">
          <div className="bg-white border border-[#E8E4DC] rounded-2xl px-4 py-3" data-testid="activity-summary">
            <p className="text-[10px] text-[#D4AF37] uppercase tracking-[0.25em] font-bold mb-1 flex items-center gap-1.5">
              <span className="text-base" aria-hidden="true">{activity.emoji}</span>
              Activité · {activity.title}
            </p>
            <p className="text-[13px] text-[#1A2F4A]/90 leading-relaxed">{activity.subtitle}</p>
            <p className="text-[11px] text-[#6B4E37] italic mt-1">Temps attendu : {activity.pace}</p>
          </div>

          <div className="bg-white border border-[#E8E4DC] rounded-2xl px-4 py-3" data-testid="timer-controls">
            <p className="text-[10px] text-[#6B4E37] uppercase tracking-[0.2em] font-bold mb-1.5">Mode du chrono</p>
            <div className="grid grid-cols-3 gap-1.5" data-testid="timer-modes">
              {timerModes.map((t) => {
                const isActive = mode === t.id;
                return (
                  <button
                    key={t.id}
                    onClick={() => setMode(t.id)}
                    className={`rounded-full text-[11px] font-bold py-1.5 border transition-colors ${
                      isActive
                        ? 'bg-[#1A2F4A] text-white border-[#1A2F4A]'
                        : 'bg-white text-[#1A2F4A] border-[#E8E4DC] hover:border-[#D4AF37]'
                    }`}
                    data-testid={`timer-mode-${t.id}`}
                  >
                    {t.name} <span className="opacity-70 font-medium">· {t.sub}</span>
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* Active prompt detail */}
      <section className="pb-8 pt-4">
        <div className="max-w-[760px] mx-auto px-6">
          <PromptDetail activity={activity} prompt={prompt} timerSeconds={timerSeconds} level={level} />
        </div>
      </section>

      {/* Prompt grid */}
      <section className="pb-20">
        <div className="max-w-[760px] mx-auto px-6">
          <div className="flex items-center justify-between mb-3">
            <p className="text-[10px] text-[#6B4E37] uppercase tracking-[0.25em] font-bold">
              {activity.promptLabel}s · {activity.title}
            </p>
            <p className="text-[10px] text-[#999]">{activity.prompts.length} prompts</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2" data-testid="prompt-grid">
            {activity.prompts.map((p, idx) => {
              const isActive = p.id === promptId;
              return (
                <button
                  key={p.id}
                  onClick={() => goToPrompt(p.id)}
                  className={`group rounded-xl px-3 py-3 text-left transition-all border ${
                    isActive
                      ? 'bg-[#1A2F4A] text-white border-[#1A2F4A]'
                      : 'bg-white text-[#1A2F4A] border-[#E8E4DC] hover:border-[#D4AF37] hover:shadow-sm'
                  }`}
                  data-testid={`prompt-tile-${p.id}`}
                >
                  <div className="flex items-start gap-2">
                    <span
                      className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 text-[10px] font-bold ${
                        isActive ? 'bg-[#D4AF37] text-[#1A2F4A]' : 'bg-[#FAF7F0] text-[#1A2F4A] border border-[#E8E4DC]'
                      }`}
                    >
                      {idx + 1}
                    </span>
                    <div className="min-w-0 flex-1">
                      <p className={`text-[9px] uppercase tracking-wider font-bold ${isActive ? 'text-[#D4AF37]' : 'text-[#999]'}`}>
                        {activity.promptLabel} {idx + 1}
                      </p>
                      <p className="text-[11px] font-medium leading-snug mt-0.5 line-clamp-2">{p.audio}</p>
                    </div>
                    {isActive && <Play className="w-3 h-3 text-[#D4AF37] mt-0.5 flex-shrink-0" />}
                  </div>
                </button>
              );
            })}
          </div>

          {/* Objective note */}
          <div className="mt-6 bg-white border border-[#E8E4DC] rounded-2xl px-5 py-4" data-testid="objective-note">
            <p className="text-[10px] text-[#D4AF37] uppercase tracking-[0.25em] font-bold mb-2" data-testid="objective-title">{objective.title}</p>
            <p className="text-[12px] text-[#1A2F4A]/90 italic mb-2">{objective.intro}</p>
            <ul className="space-y-1.5 text-[12px] text-[#1A2F4A]/90 leading-relaxed">
              {objective.items.map((g, i) => (
                <li key={i} className="flex items-start gap-2" data-testid={`objective-row-${i}`}>
                  <CheckCircle2 className="w-3.5 h-3.5 text-[#D4AF37] mt-0.5 flex-shrink-0" />
                  <span>{g}</span>
                </li>
              ))}
            </ul>
            <p className="text-[11px] text-[#6B4E37] italic mt-3 pt-3 border-t border-[#F3F4F6]" data-testid="objective-closing">
              {objective.closing}
            </p>
          </div>
        </div>
      </section>
    </div>
  );
}
